import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Col,
  Divider,
  message,
  Row,
  Select,
  Table,
} from 'antd';
import { connect, useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Form } from '@ant-design/compatible';
import {
  getUserGroupsAction,
  saveUserGroupAction,
} from '../../actions/UserActions';
import CustomAddNewPageContent from '../../components/Content/CustomAddNewPageContent';
import checkEnabledFeatures from '../../components/Authorization/Feature';
import { fetchData } from '../../nextgen/service/dataServices';
import { useTranslation } from 'react-i18next';

const { Option } = Select;

function findObjectBySelectedKey(list, key) {
  if (list) {
    const selected = list.filter(item => item.id === key);
    if (selected.length === 1) {
      return selected[0];
    }
  }

  return [];
}

const PermissionPage = ({ form }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const [selected, setSelected] = useState(null);
  const [formSchemas, setFormSchemas] = useState([]);

  const { userGroups, currentUser } = useSelector(store => ({
    userGroups: store.userReducer.userGroups,
    currentUser: store.userReducer.currentUser,
  }));

  useEffect(() => {
    dispatch(getUserGroupsAction());
  }, []);

  fetchData('/api/form-schema', data => setFormSchemas(data || []));

  fetchData('/api/form-schema', data => setFormSchemas(data || []));

  useEffect(() => {
    if (!selected) return;
    const fieldValues = {};
    rows.filter(r => !r.isDivider).forEach(record => {
      fieldValues[record.key + '_read'] = getInitialValue(record, 'read');
      fieldValues[record.key + '_write'] = getInitialValue(record, 'write');
    });
    form.setFieldsValue(fieldValues);
  }, [selected]);

  const handleUserGroupChange = value => {
    if (value === undefined) {
      setSelected(null);
      form.resetFields();
    } else {
      const selectedObj = findObjectBySelectedKey(userGroups, value);
      setSelected(selectedObj);
    }
  };

  const schemasLoaded = formSchemas.length > 0;

  const handleSubmit = e => {
    e.preventDefault();

    if (!schemasLoaded) return;

    form.validateFields((err, values) => {
      if (!err && selected !== null) {
        const formTypes = {};
        formSchemas.forEach(schema => {
          const readKey = `formTypes_${schema.id}_read`;
          const writeKey = `formTypes_${schema.id}_write`;
          formTypes[schema.id] = {
            read: values[readKey] || false,
            write: values[writeKey] || false,
          };
        });

        const dataToSend = {
          id: selected.id,
          value: selected.value,
          permission: {
            item: {
              read: values.item_read,
              write: values.item_write,
            },
            setting: {
              read: values.setting_read,
              write: values.setting_write,
            },
            user: {
              read: values.user_read,
              write: values.user_write,
            },
            appcms: {
              read: values.appcms_read,
              write: values.appcms_write,
            },
            formSchema: {
              read: values.formSchema_read,
              write: values.formSchema_write,
            },
            formTypes: formTypes,
          },
        };

        dispatch(saveUserGroupAction(dataToSend));
        message.success(t('permissions.permissionsSaved'));
      }
    });
  };

  let rows = [
    {
      key: 'user',
      name: t('permissions.userManagement'),
    },
    {
      key: 'setting',
      name: t('permissions.settings'),
    },
  ];

  if (checkEnabledFeatures(currentUser, 'Item-Cataloging')) {
    rows = [
      ...rows,
      {
        key: 'item',
        name: t('permissions.items'),
      },
      {
        key: 'formSchema',
        name: t('permissions.formManagement'),
      },
    ];
  }

  if (checkEnabledFeatures(currentUser, 'App-Cms')) {
    rows = [
      ...rows,
      {
        key: 'appcms',
        name: t('permissions.appCms'),
      },
    ];
  }

  if (formSchemas.length > 0) {
    rows = [
      ...rows,
      { key: 'formTypesDivider', name: t('permissions.formTypes'), isDivider: true },
      ...formSchemas.map(schema => ({
        key: `formTypes_${schema.id}`,
        name: schema.name?.[i18n.language] || schema.name?.['en'] || schema.id,
        isFormType: true,
      })),
    ];
  }

  const getInitialValue = (record, operation) => {
    if (!selected || !selected.permission) return false;
    if (record.isFormType) {
      const schemaId = record.key.replace('formTypes_', '');
      return selected.permission.formTypes &&
        selected.permission.formTypes[schemaId] &&
        selected.permission.formTypes[schemaId][operation]
        ? true
        : false;
    }
    return selected.permission[record.key]
      ? selected.permission[record.key][operation]
      : false;
  };

  const columns = [
    {
      title: t('permissions.menuItems'),
      dataIndex: 'name',
      key: 'menuItem',
      align: 'center',
    },
    {
      title: t('permissions.read'),
      key: 'read',
      align: 'center',
      render: (text, record) => {
        if (record.isDivider) return null;
        return (
          <div style={{ justifyContent: 'center' }}>
            <Form.Item style={{ justifyContent: 'center' }}>
              {form.getFieldDecorator(record.key + '_read', {
                rules: [],
                valuePropName: 'checked',
                initialValue: getInitialValue(record, 'read'),
              })(<Checkbox />)}
            </Form.Item>
          </div>
        );
      },
    },
    {
      title: t('permissions.write'),
      key: 'write',
      align: 'center',
      render: (text, record) => {
        if (record.isDivider) return null;
        return (
          <div style={{ justifyContent: 'center' }}>
            <Form.Item style={{ justifyContent: 'center' }}>
              {form.getFieldDecorator(record.key + '_write', {
                rules: [],
                valuePropName: 'checked',
                initialValue: getInitialValue(record, 'write'),
              })(<Checkbox />)}
            </Form.Item>
          </div>
        );
      },
    },
  ];

  return (
    <CustomAddNewPageContent
      title={t('permissions.userGroupPermissions')}
      subTitle={t('users.userManagement')}
    >
      <Select
        placeholder={t('permissions.selectUserGroup')}
        style={{ width: 256 }}
        allowClear
        showSearch
        filterOption
        optionFilterProp="children"
        onChange={handleUserGroupChange}
      >
        {userGroups.map(item => (
          <Option key={item.id} value={item.id}>
            {item.value}
          </Option>
        ))}
      </Select>
      <Divider />
      <Form onSubmit={handleSubmit}>
        <Row type="flex" justify="center">
          <Col span={16}>
            <Table
              columns={columns}
              dataSource={rows}
              pagination={false}
              rowClassName={(record) => record.isDivider ? 'form-types-divider-row' : ''}
            />
          </Col>
        </Row>
        <Row type="flex" justify="center" style={{ marginTop: 30 }}>
          <Button type="primary" htmlType="submit" disabled={!selected || !schemasLoaded}>
            {t('common.save')}
          </Button>
        </Row>
      </Form>
    </CustomAddNewPageContent>
  );
};

PermissionPage.propTypes = {
  form: PropTypes.object,
};

const WrappedPermissionPage = Form.create({ name: 'permission' })(
  PermissionPage,
);

function mapStateToProps({ userReducer }) {
  const { userGroups } = userReducer;
  return { userGroups };
}

const WiredPermissionPage = connect(mapStateToProps, {
  saveUserGroupAction,
  getUserGroupsAction,
})(WrappedPermissionPage);

export default WiredPermissionPage;
